/**
 * LEGACY experience (every store not on the Service Desk pilot list). Kept as it
 * was on main. The help slide-out and the report-an-issue dialog, mounted once for the whole
 * app. Both are the shared components from `@ampersandbrands/ui`; this file
 * supplies the Sweet Spot half: the audience-scoped client, the store's tool
 * list, and the route the reader is on.
 *
 * Rendered from the header's help and bug buttons, and from the help panel's
 * own "Report an Issue" row.
 */

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { HelpPanel, ReportIssueDialog } from '@ampersandbrands/ui';
import type { SupportScreenshot } from '@/lib/supportScreenshot';
import { useSession } from '@/lib/useSession';
import { useStoreAssignment } from '@/lib/useStores';
import {
  helpArticleForPath,
  helpClient,
  submitKbFeedback,
  SWEET_SPOT_TOOL_OPTIONS,
  toolLabelForPath,
  useCreateSupportIssue,
  type PageStateEntry,
} from '@/lib/legacyHelpClient';

export function LegacyHelpAndSupport({
  helpOpen,
  onHelpClose,
  reportOpen,
  onReportOpen,
  onReportClose,
  screenshot,
  articleOverride,
}: {
  helpOpen: boolean;
  onHelpClose: () => void;
  reportOpen: boolean;
  onReportOpen: () => void;
  onReportClose: () => void;
  screenshot?: SupportScreenshot | null;
  /** Confluence page id from a ?help_article= link; opens the slide-out on that page instead of the page's own article. */
  articleOverride?: string | null;
}) {
  const location = useLocation();
  const { user } = useSession();
  const createIssue = useCreateSupportIssue();
  const storeAssignment = useStoreAssignment();
  const [tool, setTool] = useState<string | undefined>(undefined);

  // Gate for the "Improve this article" box at the bottom of an open help
  // article (spec: KB-article feedback box, 2026-08-09). Same accessSlug as
  // Cockpit's console-only KB Feedback row (admin.kb-feedback); a corporate
  // user reaches The Sweet Spot with a grant or as a super-admin, a store
  // account never holds this slug and never sees the box.
  const canSubmitFeedback = user?.superAdmin === true || (user?.permissions ?? []).includes('admin.kb-feedback');

  // Preselect the tool the reader is looking at, refreshed each time the dialog
  // opens so a report filed later in the session is not stamped with an old page.
  useEffect(() => {
    if (reportOpen) setTool(toolLabelForPath(location.pathname));
  }, [reportOpen, location.pathname]);

  useEffect(() => {
    if (reportOpen) createIssue.reset();
    // createIssue is a new object each render; resetting on open is the intent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportOpen]);

  const route = `${location.pathname}${location.search}`;

  // Page state the api can't recover from the URL alone. Today that's just
  // the corporate store picker: a store account is locked to its own store
  // (no picker to report), so this only applies to a corporate user who has
  // picked one via the header switcher (persisted in localStorage, not the
  // query string).
  const pageState: PageStateEntry[] | undefined =
    storeAssignment.kind === 'corporate' && storeAssignment.storeNo !== undefined
      ? [{ label: 'Selected store', value: `${storeAssignment.storeNo} ${storeAssignment.storeName}` }]
      : undefined;

  return (
    <>
      <HelpPanel
        open={helpOpen}
        onClose={onHelpClose}
        client={helpClient}
        initialPageId={articleOverride ?? helpArticleForPath(location.pathname)}
        pinnedCategory="The Sweet Spot"
        onReportIssue={() => {
          onHelpClose();
          onReportOpen();
        }}
        feedback={{
          canSubmit: canSubmitFeedback,
          submit: ({ pageId, articleTitle, instruction }) =>
            submitKbFeedback({ pageId, articleTitle, instruction, route }),
        }}
      />
      {reportOpen && (
        <ReportIssueDialog
          productName="The Sweet Spot"
          toolLabel="Part of the app"
          toolOptions={[...SWEET_SPOT_TOOL_OPTIONS]}
          initialTool={tool}
          route={route}
          screenshot={screenshot ?? null}
          screenshotAttempted
          onClose={onReportClose}
          onSubmit={(input) =>
            // Field by field, not a spread, so a new field on the shared
            // dialog is a deliberate choice here rather than a silent
            // pass-through. Anything added there has to be added here too.
            createIssue.mutate({
              kind: input.kind,
              tool: input.tool,
              summary: input.summary,
              description: input.description,
              description_html: input.description_html,
              priority: input.priority,
              route: input.route,
              // The dialog seeds the auto-captured screenshot as the first
              // attachment, so it rides along in this list. It is only sent
              // as `screenshot` when the reporter attached nothing, and it is
              // not restored from the prop: an empty list means the reporter
              // removed it on purpose.
              screenshot: input.screenshot ?? null,
              attachments: input.attachments,
              page_state: pageState,
            })
          }
          isSubmitting={createIssue.isPending}
          error={createIssue.error ? createIssue.error.message : null}
          ticket={createIssue.data?.ticket ?? null}
        />
      )}
    </>
  );
}
